// Shared building blocks for every menu screen: one frame, cards, chips, badges, buttons. Screens return HTML
// strings; styles live in client/index.html (.mn-*). Nothing here touches the DOM or the server.
import { RARITIES, WORLD } from '../../../common/src/data';
import type { Rarity } from '../../../common/src/data';
import type { Character, Derived } from '../../../common/src/rules';
import type { PlayerSnap, Snapshot } from '../../../common/src/protocol';
import { TILE } from '../../../common/src/map';

export const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

// Purchase request ids must stay identical across re-renders (so an unchanged menu is not rebuilt under the
// player's finger), and change after every tap (so a second tap is a new purchase). Retries of the same
// message stay deduplicated server-side.
let reqSeed = Math.random().toString(36).slice(2); let reqN = 0;
export const rid = () => `${reqSeed}-${reqN++}`;
export const resetRid = () => { reqN = 0; };
export const newSeed = () => { reqSeed = Math.random().toString(36).slice(2); };

/** Which sub-view, selection, filter and sort each screen shows. */
export interface View { sub: string; item: string; perk: string; filter: string; sort: string; gs: string; compare?: string; page?: string }
export interface Settings { crt: boolean; lowFx: boolean; volume: number; radio: number; sfx: number; mute: boolean; radioMute: boolean; zoom: number; light: boolean; neon: number; goal: boolean }
/** Everything a screen needs to draw itself. */
export interface Ctx {
  c: Character; d: Derived; snap: Snapshot | null; me: PlayerSnap | undefined; you: number; code: string;
  view: View; settings: Settings;
  /** true when the player stands at a point of interest of this kind (the server enforces the same 64 px) */
  near: (kind: string) => boolean;
}
export interface Screen { title: string; chips?: string; body: string; foot?: string; subs?: string }

/** The player is within the server's reach of a point of interest. */
export const nearPoi = (me: PlayerSnap | undefined, kind: string, r = 64) =>
  !!me && WORLD.pois.some(o => o.kind === kind && Math.hypot(o.x * TILE - me.x, o.y * TILE - me.y) < r);

export const btn = (label: string, act: object, k: string, disabled = false, cls = '') =>
  `<button class="${cls}" data-act='${esc(JSON.stringify(act))}' data-k="${esc(k)}" ${disabled ? 'disabled' : ''}>${label}</button>`;
/** A button that changes what the menu shows (sub-tab, selection, filter, sort), not the game. */
export const pick = (key: keyof View, value: string, label: string, on: boolean, cls = '') =>
  `<button class="${cls} ${on ? 'on' : ''}" data-view="${key}:${esc(value)}" data-${key}="${esc(value)}" data-k="${key}-${esc(value)}" aria-pressed="${on}">${label}</button>`;

export const rarityColor = (r: Rarity) => RARITIES.tiers[r].color;
export const rarityBadge = (r: Rarity) => `<span class="mn-rar" style="--rc:${rarityColor(r)}">${RARITIES.tiers[r].name}</span>`;
export const chip = (label: string, value = '', tone: '' | 'good' | 'bad' | 'warn' = '') =>
  `<span class="mn-chip ${tone}">${label ? `<i>${label}</i>` : ''}${value}</span>`;
export const section = (title: string, hint = '') => `<div class="mn-sec"><h3>${title}</h3>${hint ? `<span class="dim">${hint}</span>` : ''}</div>`;
export const note = (text: string, tone: '' | 'bad' | 'good' = '') => `<p class="mn-note ${tone}">${text}</p>`;
export const lockNote = (text: string) => `<span class="mn-lock">Locked · ${text}</span>`;
export const empty = (text: string) => `<div class="mn-empty">${text}</div>`;
export const money = (n: number) => `$${n.toLocaleString('en-US')}`;
export const grid = (cls: string, inner: string) => `<div class="${cls}">${inner}</div>`;
/** Sub-tab strip: [id, label, badge]. */
export const subtabs = (items: [string, string, number?][], cur: string) =>
  `<div class="mn-subs">${items.map(([id, label, n]) => pick('sub', id, `${label}${n ? ` <b class="mn-badge">${n}</b>` : ''}`, id === cur, 'mn-sub')).join('')}</div>`;
/** A stat difference: green when it improves, red when it worsens, nothing when equal. */
export function delta(v: number, fmt: (x: number) => string, goodUp: boolean) {
  if (Math.abs(v) < 1e-6) return '';
  return `<span class="mn-d ${(v > 0) === goodUp ? 'good' : 'bad'}">${v > 0 ? '+' : ''}${fmt(v)}</span>`;
}
export const bar = (v: number, max: number) => `<span class="mn-pips">${Array.from({ length: max }, (_, i) => `<i class="${i < v ? 'on' : ''}"></i>`).join('')}</span>`;
/** The name of the nearest counter of this kind to the player (several clinics and chop yards exist). */
export const vendorName = (kind: string, me?: PlayerSnap) => {
  const all = WORLD.pois.filter(p => p.kind === kind), d = (p: { x: number; y: number }) => me ? Math.hypot(p.x * TILE - me.x, p.y * TILE - me.y) : 0;
  return all.sort((a, b) => d(a) - d(b))[0]?.name ?? kind;
};
